import React, { useEffect, useRef, useState } from 'react'
import { Platform, Pressable, TextInput, View } from 'react-native'
import Animated, {
  Easing,
  SharedValue,
  WithSpringConfig,
  interpolate,
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withDelay,
  withRepeat,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import cn from '@ui/utils/cn'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'

const borderRadius = 16

const Digit = ({
  value,
  focus,
  focusPos,
  index,
  handleFocus,
  prevValue,
  cursor,
  invalidAnim,
}: {
  value: string
  prevValue: string
  cursor: SharedValue<number>
  focus: SharedValue<number>
  focusPos: SharedValue<number>
  invalidAnim: SharedValue<number>
  index: number
  handleFocus: (index: number) => void
}) => {
  const isFocused = useDerivedValue(() => {
    const currentFocus = interpolate(
      focusPos.value,
      [index - 1, index, index + 1],
      [0, 1, 0]
    )
    return currentFocus * focus.value
  })

  const bgColors = Platform.select({
    default: [colors.white25, colors.white],
    web: [colors.white25, colors.white],
  })

  const style = useAnimatedStyle(() => {
    // const borderColor = interpolateColor(
    //   invalidAnim.value,
    //   [0, 1],
    //   [colors.black10, colors.red10]
    // )

    const borderColor = interpolateColor(
      isFocused.value,
      [0, 1],
      invalidAnim.value
        ? [colors.red0, colors.red[500]]
        : [colors.black10, colors.black]
    )

    const backgroundColor = interpolateColor(
      isFocused.value,
      [0, 1],
      invalidAnim.value
        ? [colors.red[100], colors.white]
        : [colors.white0, colors.white]
    )

    return {
      opacity: 1,
      borderColor,
      borderWidth: 2,
      backgroundColor,
    }
  })

  const cursorStyle = useAnimatedStyle(() => {
    if (!Number.isInteger(focusPos.value) || isFocused.value !== 1 || !!value) {
      return {
        opacity: 0,
      }
    }

    return {
      opacity: cursor.value,
    }
  })

  const styleText = useAnimatedStyle(() => {
    const color = interpolateColor(
      invalidAnim.value,
      [0, 1],
      [colors.text, colors.red[600]]
    )

    return {
      color,
    }
  })

  return (
    <Pressable onPress={() => handleFocus(index)}>
      <Animated.View
        style={[style, { borderRadius }]}
        className="h-16 w-16 items-center justify-center"
      >
        {!!value && (
          <Typography variant="XLB" style={styleText}>
            {value}
          </Typography>
        )}
        {!value && !!prevValue && (
          <Typography variant="XLB" className={cn('color-text/10')}>
            {prevValue}
          </Typography>
        )}
        <Animated.View
          style={cursorStyle}
          className="bg-text absolute bottom-4 top-4 w-px"
        />
      </Animated.View>
    </Pressable>
  )
}

const springConfig: WithSpringConfig = {
  damping: 10,
  stiffness: 150,
  overshootClamping: true,
}

const CodeInput = ({
  length = 6,
  onSubmit,
  setValue,
  value,
  invalid,
}: {
  invalid?: boolean
  value: string
  setValue: (v: string) => void
  length?: number
  onSubmit?: (v: string) => void
}) => {
  const input = useRef<TextInput>(null)

  const focus = useSharedValue(0)
  const focusPos = useSharedValue(0)
  const invalidAnim = useAnimatedToggle(!!invalid)
  const [prevValue, setPrevValue] = useState('')

  const cursor = useSharedValue(0)

  useEffect(() => {
    cursor.set(
      withRepeat(
        withDelay(
          600,
          withSequence(
            withTiming(1, {
              easing: Easing.ease,
              duration: 400,
            }),
            withTiming(0, {
              easing: Easing.ease,
              duration: 400,
            })
          )
        ),
        -1
      )
    )
  }, [])

  const handleFocus = (index: number) => {
    input.current?.focus()
    focus.value = withSpring(1, springConfig)
    focusPos.value = withSpring(Math.min(value.length, index), springConfig)
    setPrevValue(value.substring(0, index + 1))
    setValue(value.substring(0, index))
  }

  const handleBlur = () => {
    focus.value = withSpring(0, springConfig)
  }

  useEffect(() => {
    focusPos.value = withSpring(
      Math.min(value.length, length - 1),
      springConfig
    )
  }, [value.length])

  const nums = Array.from({ length })
  const vals = Array.from(value)

  const prevVals = Array.from(prevValue)

  const onChange = (text: string) => {
    setValue(text.substring(0, length))
    focus.value = withSpring(1, springConfig)
    setPrevValue('')
    if (text.length === length) {
      onSubmit?.(value)
      input.current?.blur()
      return
    }
  }

  return (
    <View className="flex-row gap-4">
      {nums.map((_, index) => (
        <Digit
          key={index}
          value={vals[index] || ''}
          prevValue={prevVals[index] || ''}
          index={index}
          focus={focus}
          invalidAnim={invalidAnim}
          focusPos={focusPos}
          handleFocus={handleFocus}
          cursor={cursor}
        />
      ))}
      <TextInput
        ref={input}
        value={value}
        keyboardType="default"
        onChangeText={onChange}
        onBlur={handleBlur}
        className="pointer-events-none absolute opacity-0"
        keyboardAppearance="dark"
      />
    </View>
  )
}

export default CodeInput
